var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from "react";
import { AppStateContext } from "./AppStateProvider";
import websocketHandler from "./websocketHandler";
import { TimerContext } from "./TimerProvider";
import memoize from "./memoize";
import WasmContext from "./WasmContext";
import { isWasmAvailable } from "./detectWasm";
export var WebsocketContext = React.createContext({
    send: function () { },
    reconnectNow: function () { },
});
var getFileReader = memoize(function () {
    var queue = [];
    var fr = new FileReader();
    fr.onload = function () {
        var next = queue.shift();
        if (next !== undefined) {
            next.handler(fr.result);
            if (queue.length > 0) {
                fr.readAsArrayBuffer(queue[0].blob);
            }
        }
    };
    return {
        enqueue: function (blob, handler) {
            queue.push({ blob: blob, handler: handler });
            if (queue.length > 0 &&
                (fr.readyState === FileReader.EMPTY ||
                    fr.readyState === FileReader.DONE)) {
                fr.readAsArrayBuffer(queue[0].blob);
            }
        },
    };
});
var getBlobArrayBuffer = memoize(function () {
    var queue = [];
    var inflight = [];
    var onload = function (arr) {
        var next = queue.shift();
        if (next !== undefined) {
            inflight.shift();
            next.handler(arr);
            if (queue.length > 0) {
                inflight.push(0);
                queue[0].blob.arrayBuffer().then(onload, function (err) { return console.log(err); });
            }
        }
    };
    return {
        enqueue: function (blob, handler) {
            queue.push({ blob: blob, handler: handler });
            if (inflight.length === 0 && queue.length > 0) {
                inflight.push(0);
                blob.arrayBuffer().then(onload, function (err) { return console.log(err); });
            }
        },
    };
});
var WebsocketProvider = function (props) {
    var _a = React.useContext(AppStateContext), state = _a.state, updateState = _a.updateState;
    var decodeWireFormat = React.useContext(WasmContext).decodeWireFormat;
    var _b = React.useContext(TimerContext), setTimeout = _b.setTimeout, clearTimeout = _b.clearTimeout;
    var _c = React.useState(null), timer = _c[0], setTimer = _c[1];
    var _d = React.useState(null), websocket = _d[0], setWebsocket = _d[1];
    // Auto-reconnect bookkeeping. On an unexpected drop while seated in a room we
    // recreate the socket (exponential backoff) and replay the JoinRoom handshake
    // to reclaim the same seat. A `Kicked` message or a deliberate leave suppress
    // reconnection.
    var wsRef = React.useRef(null);
    var deliberateCloseRef = React.useRef(false);
    var reconnectAttemptsRef = React.useRef(0);
    var reconnectTimerRef = React.useRef(null);
    var connectRef = React.useRef(function () { });
    // Because state/updateState are passed in and change every time something
    // happens, we need to maintain a reference to these props to prevent stale
    // closures which may happen if state/updateState is changed between when an
    // event listener is registered and when it fires.
    // https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
    var stateRef = React.useRef(state);
    var updateStateRef = React.useRef(updateState);
    var timerRef = React.useRef(timer);
    var setTimerRef = React.useRef(setTimer);
    var setTimeoutRef = React.useRef(setTimeout);
    var clearTimeoutRef = React.useRef(clearTimeout);
    React.useEffect(function () {
        stateRef.current = state;
        updateStateRef.current = updateState;
    }, [state, updateState]);
    React.useEffect(function () {
        setTimeoutRef.current = setTimeout;
        clearTimeoutRef.current = clearTimeout;
    }, [setTimeout, clearTimeout]);
    React.useEffect(function () {
        timerRef.current = timer;
        setTimerRef.current = setTimer;
    }, [timer, setTimerRef]);
    React.useEffect(function () {
        var computeUri = function () {
            // Resolution order for the game WebSocket host:
            //   1. window._WEBSOCKET_HOST  — injected at runtime by the backend's
            //      /runtime.js (set from the WEBSOCKET_HOST env var). Present for the
            //      single-service deploy.
            //   2. process.env.WEBSOCKET_HOST — baked into the bundle at build time via
            //      webpack DefinePlugin. Used by a standalone frontend (e.g. Vercel).
            //   3. same-origin — the original default.
            var runtimeWebsocketHost = window._WEBSOCKET_HOST;
            var bakedWebsocketHost = process.env.WEBSOCKET_HOST;
            return runtimeWebsocketHost !== undefined && runtimeWebsocketHost !== null
                ? runtimeWebsocketHost
                : bakedWebsocketHost !== undefined &&
                    bakedWebsocketHost !== null &&
                    bakedWebsocketHost !== ""
                    ? bakedWebsocketHost
                    : (location.protocol === "https:" ? "wss://" : "ws://") +
                        location.host +
                        location.pathname +
                        (location.pathname.endsWith("/") ? "api" : "/api");
        };
        var scheduleReconnect = function () {
            // Only auto-reconnect after an UNEXPECTED drop while seated in a room.
            if (deliberateCloseRef.current) {
                return;
            }
            if (stateRef.current.roomName.length !== 16) {
                return;
            }
            if (reconnectTimerRef.current !== null) {
                return;
            }
            // A newer socket is already open/connecting (e.g. a stale socket closed
            // after we already reconnected) — don't pile on.
            var cur = wsRef.current;
            if (cur !== null &&
                (cur.readyState === WebSocket.OPEN ||
                    cur.readyState === WebSocket.CONNECTING)) {
                return;
            }
            updateStateRef.current({ reconnecting: true });
            var attempt = reconnectAttemptsRef.current;
            var delay = Math.min(1000 * Math.pow(2, attempt), 10000);
            reconnectAttemptsRef.current = attempt + 1;
            reconnectTimerRef.current = window.setTimeout(function () {
                reconnectTimerRef.current = null;
                connectRef.current();
            }, delay);
        };
        var connect = function () {
            var ws = new WebSocket(computeUri());
            wsRef.current = ws;
            setWebsocket(ws);
            ws.addEventListener("open", function () {
                reconnectAttemptsRef.current = 0;
                updateStateRef.current({
                    connected: true,
                    everConnected: true,
                    reconnecting: false,
                });
                // Auto-replay the JoinRoom handshake whenever we already have a sticky
                // room + name. This covers BOTH:
                //   * a reconnect after an unexpected drop (the JoinRoom form isn't
                //     mounted, so it can't send the handshake itself), and
                //   * a fresh page load / tab restore where the room was recovered from
                //     the URL hash or localStorage — we silently reclaim the same seat by
                //     name instead of dumping the player back on the JoinRoom screen.
                // The server treats a rejoin under an existing name as idempotent (see
                // shengji_handler::register_user), so this is safe even if we're the
                // first to (re)create the room. If we have no sticky room/name yet, we
                // skip this and let the JoinRoom form drive the first handshake.
                var _a = stateRef.current, roomName = _a.roomName, name = _a.name;
                if (roomName.length === 16 && name.length > 0) {
                    ws.send(JSON.stringify({
                        room_name: roomName,
                        name: name,
                        disable_compression: !isWasmAvailable(),
                    }));
                }
            });
            ws.addEventListener("close", function () {
                updateStateRef.current({ connected: false });
                scheduleReconnect();
            });
            ws.addEventListener("message", function (event) {
                if (timerRef.current !== null) {
                    clearTimeoutRef.current(timerRef.current);
                }
                setTimerRef.current(null);
                // Check if the message is text (uncompressed JSON) or binary (compressed)
                if (typeof event.data === "string") {
                    // Plain text JSON message (uncompressed)
                    try {
                        var message = JSON.parse(event.data);
                        if ("Kicked" in message) {
                            deliberateCloseRef.current = true;
                            ws.close();
                        }
                        else {
                            updateStateRef.current(__assign({ connected: true, everConnected: true, reconnecting: false }, websocketHandler(stateRef.current, message, function (msg) {
                                ws.send(JSON.stringify(msg));
                            })));
                        }
                    }
                    catch (e) {
                        console.error("Failed to parse JSON message:", e);
                    }
                }
                else {
                    // Binary message (compressed)
                    var f = function (buf) {
                        // Mirror the text branch's error handling: decodeWireFormat
                        // (zstd decompress + JSON.parse) can throw on a corrupt frame, and
                        // an uncaught throw here escapes the queue callback and can stall
                        // the message pump for queued frames.
                        try {
                            var message = decodeWireFormat(new Uint8Array(buf));
                            if (message &&
                                typeof message === "object" &&
                                "Kicked" in message) {
                                deliberateCloseRef.current = true;
                                ws.close();
                            }
                            else {
                                updateStateRef.current(__assign({ connected: true, everConnected: true, reconnecting: false }, websocketHandler(stateRef.current, message, function (msg) {
                                    ws.send(JSON.stringify(msg));
                                })));
                            }
                        }
                        catch (e) {
                            console.error("Failed to decode binary message:", e);
                        }
                    };
                    if (event.data.arrayBuffer !== undefined) {
                        var b2a = getBlobArrayBuffer();
                        b2a.enqueue(event.data, f);
                    }
                    else {
                        var frs = getFileReader();
                        frs.enqueue(event.data, f);
                    }
                }
            });
        };
        connectRef.current = connect;
        connect();
        return function () {
            if (timerRef.current !== null) {
                clearTimeoutRef.current(timerRef.current);
            }
            if (reconnectTimerRef.current !== null) {
                window.clearTimeout(reconnectTimerRef.current);
            }
        };
    }, []);
    var send = function (value, options) {
        var _a, _b;
        if (timerRef.current !== null) {
            clearTimeoutRef.current(timerRef.current);
            setTimerRef.current(null);
        }
        // We expect a response back from the server within 5 seconds. Otherwise,
        // we should assume we have lost our websocket connection. Requests flagged
        // `slowReply` opt out: the server may take longer for legitimate reasons, so
        // a slow answer is not evidence of a dead socket.
        if ((options === null || options === void 0 ? void 0 : options.slowReply) !== true) {
            var localTimerRef_1 = setTimeoutRef.current(function () {
                if (timerRef.current === localTimerRef_1) {
                    updateStateRef.current({ connected: false });
                }
            }, 5000);
            setTimerRef.current(localTimerRef_1);
        }
        (_b = ((_a = wsRef.current) !== null && _a !== void 0 ? _a : websocket)) === null || _b === void 0 ? void 0 : _b.send(JSON.stringify(value));
    };
    // Manual "Reconnect now" — reset backoff and reconnect immediately, replaying
    // the JoinRoom handshake. Used by the disconnected/reconnecting UI.
    var reconnectNow = function () {
        deliberateCloseRef.current = false;
        reconnectAttemptsRef.current = 0;
        if (reconnectTimerRef.current !== null) {
            window.clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        updateStateRef.current({ reconnecting: true });
        connectRef.current();
    };
    // TODO(read this from consumers instead of globals)
    window.send = send;
    return (React.createElement(WebsocketContext.Provider, { value: { send: send, reconnectNow: reconnectNow } }, props.children));
};
export default WebsocketProvider;
